import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, QUICK_SIGNUP_STORAGE_KEY } from '../config/envConfig';
import {
  clearQuickSignupSession,
  isAccountExistsError,
  looksLikeWebsite,
  toCompanyWebsiteUrl,
} from '../utils/quickSignupAuth';
import { openRazorpayCheckout } from '../utils/razorpayCheckout';
import { isPersonalEmail } from '../utils/emailValidation';

const STYLES = `
.wo-signin-overlay{
  position:fixed;inset:0;z-index:1000;
  display:flex;align-items:center;justify-content:center;
  padding:24px;background:rgba(12,22,48,.55);
  backdrop-filter:blur(4px);
}
.wo-signin-modal{
  position:relative;width:min(420px,100%);
  background:#fff;border-radius:16px;
  padding:36px 32px 32px;
  box-shadow:0 24px 60px rgba(8,18,28,.35);
  font-family:'Instrument Sans',sans-serif;
  color:#1a1a1a;text-align:center;
}
.wo-signin-close{
  position:absolute;top:14px;right:14px;
  width:32px;height:32px;border:none;border-radius:8px;
  background:transparent;color:#8A8A8A;font-size:1.35rem;
  line-height:1;cursor:pointer;
}
.wo-signin-close:hover{background:#F3F3F5;color:#333}
.wo-signin-modal h2{
  font-size:1.35rem;font-weight:700;margin:0 0 8px;color:#111;
}
.wo-signin-modal .wo-signin-sub{
  margin:0 0 20px;font-size:.95rem;color:#6B6B6B;line-height:1.45;
}
.wo-signin-query{
  margin:0 0 20px;padding:14px 16px;border-radius:10px;
  background:#F3F0FF;color:#5B4B8A;
  font-size:.9rem;line-height:1.45;text-align:left;
  word-break:break-word;
}
.wo-signin-form{display:flex;flex-direction:column;gap:12px}
.wo-signin-form input{
  width:100%;padding:13px 14px;border-radius:10px;
  border:1px solid #DDDDDD;background:#fff;
  font-family:inherit;font-size:.95rem;color:#1a1a1a;
  outline:none;box-shadow:none;
}
.wo-signin-form input::placeholder{color:#A0A0A0}
.wo-signin-form input:focus{border-color:#B8A9F0}
.wo-signin-email-btn{
  width:100%;padding:13px 16px;border:none;border-radius:10px;
  background:#1B2E59;color:#fff;font-family:inherit;
  font-size:.95rem;font-weight:600;cursor:pointer;
  transition:background .15s ease,transform .15s ease;
}
.wo-signin-email-btn:hover{background:#152346}
.wo-signin-email-btn:disabled{opacity:.65;cursor:not-allowed}
.wo-signin-error{
  margin:0;font-size:.82rem;color:#C0392B;text-align:left;
}
.wo-signin-divider{
  display:flex;align-items:center;gap:12px;
  margin:18px 0;color:#9A9A9A;font-size:.82rem;
}
.wo-signin-divider::before,.wo-signin-divider::after{
  content:'';flex:1;height:1px;background:#E5E5E5;
}
.wo-signin-oauth{display:flex;flex-direction:column;gap:10px}
.wo-signin-oauth-btn{
  width:100%;display:flex;align-items:center;justify-content:center;gap:10px;
  padding:12px 16px;border-radius:10px;border:1px solid #DDDDDD;
  background:#fff;color:#1a1a1a;font-family:inherit;
  font-size:.95rem;font-weight:600;cursor:pointer;
  transition:background .15s ease,border-color .15s ease;
}
.wo-signin-oauth-btn:hover{background:#F7F7F8;border-color:#CFCFCF}
.wo-signin-oauth-btn:disabled{opacity:.65;cursor:not-allowed}
.wo-signin-oauth-btn svg{width:18px;height:18px;flex-shrink:0}
`;

const stripWebsiteHost = (value = '') =>
  value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const OutlookIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#0078D4" d="M13 3.5h7.5A1.5 1.5 0 0 1 22 5v14a1.5 1.5 0 0 1-1.5 1.5H13V3.5z" />
    <path fill="#28A8EA" d="M13 12.2l7.5-4.2V5L13 9.2v3z" />
    <path fill="#50D9FF" d="M13 12.2V14.8L20.5 19v-2.8L13 12.2z" />
    <path fill="#0364B8" d="M2.5 6.5A2.5 2.5 0 0 1 5 4h8v16H5a2.5 2.5 0 0 1-2.5-2.5v-11z" />
    <circle cx="9" cy="12" r="3.2" fill="#fff" />
  </svg>
);

const SIGNUP_ERROR_MESSAGES = {
  personal_email_not_allowed: 'Please use your work email. Personal email addresses are not allowed.',
};

const resolveSignupError = (data, fallback) => {
  const code = data?.error || data?.code || '';
  if (SIGNUP_ERROR_MESSAGES[code]) return SIGNUP_ERROR_MESSAGES[code];
  return data?.error || data?.message || fallback;
};

const SignInModal = ({ open, onClose, query = '', planId = null }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkEmailMessage, setCheckEmailMessage] = useState('');
  const isBuyFlow = Boolean(planId);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, loading]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setCompanyWebsite('');
      setError('');
      setLoading(false);
      setCheckEmailMessage('');
    }
  }, [open]);

  // If the user hits Back from the Google/Outlook consent screen, the browser
  // can restore this page from bfcache with `loading` still stuck at true
  // (it was set right before the redirect and never got a chance to reset).
  // Detect that restore and clear it so the modal isn't left disabled forever.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        setLoading(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  if (!open) return null;

  const resumeOnboarding = async (userEmail) => {
    const response = await fetch(API_CONFIG.quickSignupResumeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: userEmail }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.error) {
      throw new Error(data.error || data.message || 'Unable to resume onboarding. Please try again.');
    }

    clearQuickSignupSession();
    setCheckEmailMessage(data.message || 'Check your email for a link to verify your account.');
  };

  const startOAuth = async (provider) => {
    setError('');
    setLoading(true);

    try {
      const startUrl =
        provider === 'google'
          ? API_CONFIG.quickSignupOauthGoogleStartUrl
          : API_CONFIG.quickSignupOauthMicrosoftStartUrl;
      const params = new URLSearchParams();
      if (isBuyFlow) {
        params.set('plan_id', String(planId));
      } else if (query?.trim()) {
        params.set('question', query.trim());
      }
      const qs = params.toString();
      const response = await fetch(qs ? `${startUrl}?${qs}` : startUrl);
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.auth_url) {
        throw new Error(data.error || data.message || 'Unable to start sign-in. Please try again.');
      }

      window.location.href = data.auth_url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Please enter a valid work email address');
      return;
    }
    if (isPersonalEmail(value)) {
      setError(SIGNUP_ERROR_MESSAGES.personal_email_not_allowed);
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Buy Now (manual email): register + create order, then open Checkout.js inline.
      if (isBuyFlow) {
        if (!planId) {
          setError('Please select a plan and try again.');
          return;
        }

        const websiteValue = companyWebsite.trim();
        if (!looksLikeWebsite(websiteValue)) {
          setError('Enter a valid company website (e.g. acme.com)');
          return;
        }

        const response = await fetch(API_CONFIG.pricingRegisterUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: value,
            domain: stripWebsiteHost(websiteValue),
            plan_id: planId,
          }),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.error || data.status === 'failed') {
          setError(resolveSignupError(data, 'Unable to start checkout. Please try again.'));
          return;
        }

        const order = data?.data;
        if (!order?.order_id || !order?.amount || !order?.key) {
          setError('Payment details not available. Please try again.');
          return;
        }

        openRazorpayCheckout({
          orderId: order.order_id,
          amount: order.amount,
          currency: order.currency,
          key: order.key,
          email: order.email || value,
          companyWebsite: toCompanyWebsiteUrl(websiteValue),
          navigate,
          onError: (message) => setError(message || 'Something went wrong while confirming your payment.'),
          onCancel: () => setError(''),
        });
        return;
      }

      // Free-trial signup (optional question from older prompt-bar flow).
      const response = await fetch(API_CONFIG.quickSignupStartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: value,
          question: query || '',
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (isAccountExistsError(data)) {
        setError('Account with this email already exists. Please use another email.');
        return;
      }

      if (data.error || (!response.ok && !data.resume_onboarding && !data.anon_session_id)) {
        setError(resolveSignupError(data, 'Unable to start signup. Please try again.'));
        return;
      }

      if (data.resume_onboarding) {
        await resumeOnboarding(value);
        return;
      }

      if (data.anon_session_id) {
        const payload = {
          anon_session_id: data.anon_session_id,
          suggested_website: stripWebsiteHost(data.suggested_website || ''),
          email: value,
          question: query || '',
        };
        sessionStorage.setItem(QUICK_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
        onClose?.();
        navigate('/onboarding', { state: payload });
        return;
      }

      setError(data.message || 'Unable to start signup. Please try again.');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="wo-signin-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      <style>{STYLES}</style>
      <div
        className="wo-signin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wo-signin-title"
      >
        <button
          type="button"
          className="wo-signin-close"
          aria-label="Close"
          onClick={onClose}
          disabled={loading}
        >
          ×
        </button>

        {checkEmailMessage ? (
          <>
            <h2 id="wo-signin-title">Check your email</h2>
            <p className="wo-signin-sub">{checkEmailMessage}</p>
          </>
        ) : (
          <>
            <h2 id="wo-signin-title">Sign up to WhiteOwl</h2>
            <p className="wo-signin-sub">
              {isBuyFlow
                ? ''
                : query
                  ? 'Your query is ready. Sign up to get your answer.'
                  : 'Start your free trial. No credit card required.'}
            </p>

            {!isBuyFlow && query ? (
              <div className="wo-signin-query">
                Your query: {query}
              </div>
            ) : null}

            <form className="wo-signin-form" onSubmit={handleEmailSubmit} noValidate>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Work email address"
                autoComplete="email"
                aria-label="Work email address"
                disabled={loading}
              />
              {isBuyFlow ? (
                <input
                  type="text"
                  value={companyWebsite}
                  onChange={(e) => {
                    setCompanyWebsite(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Company website (e.g. acme.com)"
                  autoComplete="url"
                  aria-label="Company website"
                  disabled={loading}
                />
              ) : null}
              {error ? <p className="wo-signin-error">{error}</p> : null}
              <button type="submit" className="wo-signin-email-btn" disabled={loading}>
                {loading ? 'Continuing…' : 'Continue with email'}
              </button>
            </form>

            <div className="wo-signin-divider">or continue with</div>

            <div className="wo-signin-oauth">
              <button
                type="button"
                className="wo-signin-oauth-btn"
                onClick={() => startOAuth('google')}
                disabled={loading}
              >
                <GoogleIcon />
                Continue with Google
              </button>
              <button
                type="button"
                className="wo-signin-oauth-btn"
                onClick={() => startOAuth('microsoft')}
                disabled={loading}
              >
                <OutlookIcon />
                Continue with Outlook
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignInModal;
